import { Position } from 'reactflow';
import { HandleType } from '@/enums/handle-type.enum';
import { getHandleId } from '../Utils';
import DynamicHandleLabel from './DynamicHandleLabel';
import { CollapsedGroupHandle } from './CollapsedGroupHandle';
import { useHandleCollapseAnalytics } from './useHandleCollapseAnalytics';
import type { ComponentProps } from 'react';
import type { HandleRow } from './handleCollapse';

interface CollapsedInputHandlesProps {
  rows: HandleRow[];
  isConnecting?: boolean;
  showGroupLabels?: boolean;
  nodeId: string;
  nodeType: string | undefined;
  handleTop: string;
  invalidHandles: { required?: string[]; requiredEmpty?: string[] } | undefined;
  getDescription: (key: string, fallback: string | undefined) => string | undefined;
  getLabel: (key: string, fallback: string | undefined) => string;
  getType: (key: string, fallback: HandleType | undefined) => HandleType;
  isGroupExpanded: (memberKeys: string[]) => boolean;
  groupHasError: (memberKeys: string[]) => boolean;
  onToggleGroup: (type: HandleType, memberCount: number) => void;
  /** Spread onto every {@link DynamicHandleLabel}, as the flat list does. */
  commonHandleProps: Pick<
    ComponentProps<typeof DynamicHandleLabel>,
    'isSelectedNode' | 'nodeId' | 'shouldObscureHandle' | 'shouldShowHandleLabel' | 'handleColor' | 'handleSpace'
  >;
}

/**
 * A node's input handles while collapse-by-type is on: one group handle per collapsed type, and a
 * normal handle for every input that is expanded or the only one of its type.
 *
 * Kept apart from the flat list so the flag-off path renders exactly the code it always did. Also
 * where {@link useHandleCollapseAnalytics}'s group-toggle tracking lives: this component only mounts
 * when the flag is on, unlike `DynamicNodeHandles`, which mounts for every v2/v3 node regardless.
 */
export const CollapsedInputHandles = ({
  rows,
  isConnecting = false,
  showGroupLabels = false,
  nodeId,
  nodeType,
  handleTop,
  invalidHandles,
  getDescription,
  getLabel,
  getType,
  isGroupExpanded,
  groupHasError,
  onToggleGroup,
  commonHandleProps,
}: CollapsedInputHandlesProps) => {
  const { trackGroupToggled } = useHandleCollapseAnalytics({ nodeId, nodeType });

  return (
    <>
      {rows.map((row) => {
        if (row.kind === 'group') {
          const isExpanded = isGroupExpanded(row.memberKeys);
          const isObscured = isConnecting;
          return (
            <CollapsedGroupHandle
              key={`group-${row.type}`}
              type={row.type}
              memberCount={row.memberKeys.length}
              index={row.rowIndex}
              handleTop={handleTop}
              isExpanded={isExpanded}
              hasError={groupHasError(row.memberKeys)}
              isObscured={isObscured}
              showLabel={showGroupLabels}
              onToggle={() => {
                // `onToggleGroup` (useHandleCollapse.toggleGroup) only writes the new state; report
                // it here so the write can never persist without also being tracked, matching
                // useHandleCollapse.toggleGroup's original wasExpanded/expanded computation.
                onToggleGroup(row.type, row.memberKeys.length);
                trackGroupToggled({ handleType: row.type, memberCount: row.memberKeys.length, expanded: !isExpanded });
              }}
            />
          );
        }

        return (
          <DynamicHandleLabel
            key={row.key}
            description={getDescription(row.key, row.handle.description)}
            format={row.handle.format}
            handleId={getHandleId(nodeId, 'input', row.key)}
            handlePosition={Position.Left}
            handleSide="target"
            handleTop={handleTop}
            handleType={getType(row.key, row.handle.type)}
            index={row.rowIndex}
            isCollapsed={row.isCollapsed}
            label={getLabel(row.key, row.handle.label)}
            required={row.handle.required}
            validationErrors={{
              required: invalidHandles?.required?.includes(row.key),
              requiredEmpty: invalidHandles?.requiredEmpty?.includes(row.key),
            }}
            isCollapseManaged
            {...commonHandleProps}
          />
        );
      })}
    </>
  );
};
